import * as React from 'react';

import QrCreator from 'qr-creator';

import socrativeLogo from './socrative-logo.png';

export interface UseQrCodeParams {
  // the text encoded in the QR code
  text: string;
  // for rounded squares, set a radius
  radius?: number;
  // QR code error correction mode. https://www.qrcode.com/en/about/error_correction.html
  // The colour of the squares in the QR code
  fill?: string;
  // Colour of the background of the QR code (null for transparent)
  background?: string;
  // size in pixels
  size: number;
}

export function useQrCode({
  fill,
  size,
  text,
  background,
  radius,
}: UseQrCodeParams) {
  const canvasRef = React.useRef<HTMLCanvasElement>();

  const refCallback = React.useCallback(
    (canvas: HTMLCanvasElement) => {
      if (!canvas) return;

      canvasRef.current = canvas;

      // 1 Draw QR code
      QrCreator.render(
        {
          text: text,
          radius: radius,
          ecLevel: 'H',
          background: background,
          fill: fill,
          size: size,
        },
        canvas
      );

      /// 2 Draw white circle
      const context = canvas.getContext('2d');

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius2 = size * 0.2;

      context.beginPath();
      context.arc(centerX, centerY, radius2, 0, 2 * Math.PI, false);
      context.fillStyle = '#FFFFFF';
      context.fill();

      // 3. draw socrative logo
      const image = new Image();
      // On production, the image is served from a different domain, so we need to add a timestamp to the url to prevent caching
      // Reference: https://stackoverflow.com/a/49503414
      image.src = (socrativeLogo as unknown as string) + `?t=${Date.now()}`;
      // Loading an image and painting it on a canvas needs to set cross origin
      // Reference: https://stackoverflow.com/questions/22710627/tainted-canvases-may-not-be-exported#comment78383542_31653587
      image.crossOrigin = 'Anonymous';
      image.onload = function drawImage() {
        // figure out new dimensions for smaller logo relative to size of the canvas
        const newHeight = canvas.height * 0.33;
        const newWidth = canvas.width * 0.33 * (image.width / image.height);

        // draw image in center of canvas
        context.drawImage(
          image,
          canvas.width / 2 - newWidth / 2,
          canvas.height / 2 - newHeight / 2,
          newWidth,
          newHeight
        );
      };
    },
    [text, radius, background, fill, size]
  );

  return { canvasRef, refCallback };
}
